import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Lightbulb, Info } from 'lucide-react';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '../config/support';

const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2.5 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300">
    <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" />
    <div>{children}</div>
  </div>
);

const DataConnectorDocs: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-16">
      {/* Header */}
      <div>
        <Link
          to="/integrations"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Connectors
        </Link>
        <h1 className="text-3xl font-bold gradient-text">Data Connector Guide</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Everything you need to know about connecting live APIs to your AI agents.
        </p>
      </div>

      {/* 1. What is a Data Connector? */}
      <section className="card p-6 space-y-3">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">1. What is a Data Connector?</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          A Data Connector is a live API endpoint that your AI agent can call during a chat conversation
          to fetch or send real-time data. Instead of relying solely on pre-loaded knowledge, connectors
          let your agent pull live information — customer orders, account details, billing data, inventory
          levels, or anything your API can serve.
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          When a customer asks "Where is my order?", the AI agent calls your order-lookup connector,
          retrieves the live status, and responds with accurate, up-to-date information.
        </p>
      </section>

      {/* 2. How the AI Decides When to Call It */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">2. How the AI Decides When to Call It</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          The <strong>Description / AI Decision Prompt</strong> field is the single most important part of
          your connector. The AI reads this description to decide <em>when</em> and <em>why</em> to call it.
          Think of it as an instruction to the AI, not a human-facing label.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800">
            <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-1">Bad example</p>
            <p className="text-sm text-red-600 dark:text-red-300 font-mono">"Order API"</p>
          </div>
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">Good example</p>
            <p className="text-sm text-green-600 dark:text-green-300 font-mono">
              "Call this tool when the customer asks about an order status, delivery tracking, or shipment ETA. Requires the order ID."
            </p>
          </div>
        </div>

        <Tip>
          Write the description as if you are instructing the AI: tell it <em>when</em> to call,
          <em>what data</em> is required, and <em>what it returns</em>.
        </Tip>
      </section>

      {/* 3. Authentication */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">3. Authentication</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Most APIs require some form of authentication. Each connector supports these auth types:
        </p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm">
          <li><strong>Bearer Token</strong> — sent as <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</code></li>
          <li><strong>API Key</strong> — sent in a custom header (e.g. <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">X-Api-Key</code>)</li>
          <li><strong>Basic Auth</strong> — username &amp; password, Base64-encoded</li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 pt-2">Auth Source</h3>
        <div className="space-y-3">
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">Saved Token / Key (encrypted)</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              You paste your API key or token into the form. It's encrypted at rest and used for every call.
              Best for static credentials.
            </p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
            <p className="font-semibold text-sm text-gray-800 dark:text-gray-200">From Input Param (dynamic token)</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              The auth token is provided dynamically at runtime — typically from the output of another connector
              (see Connector Chaining below). You specify which input parameter holds the token.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Input Parameters */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">4. Input Parameters</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Input parameters define the data your connector needs to make its API call. Each parameter has a
          <strong> source</strong> that controls where the value comes from:
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-2 pr-4 font-semibold text-gray-800 dark:text-gray-200">Source</th>
                <th className="text-left py-2 pr-4 font-semibold text-gray-800 dark:text-gray-200">How it works</th>
                <th className="text-left py-2 font-semibold text-gray-800 dark:text-gray-200">Example</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-4 font-medium">Customer Input</td>
                <td className="py-2 pr-4">The AI asks the customer for this value during the conversation.</td>
                <td className="py-2">"What is your order number?"</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-4 font-medium">From Context</td>
                <td className="py-2 pr-4">Pulled automatically from conversation context or prior connector output.</td>
                <td className="py-2">Customer email from session</td>
              </tr>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                <td className="py-2 pr-4 font-medium">Static Value</td>
                <td className="py-2 pr-4">A fixed value you define — the same every time.</td>
                <td className="py-2">API version, tenant ID</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-medium">Verified user identity</td>
                <td className="py-2 pr-4">Filled from the signed-in user's verified identity token — never from the AI. See section 8.</td>
                <td className="py-2">User email, account ID</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Tip>
          Use the <strong>Ask Prompt</strong> field to control how the AI asks the customer for a value.
          The <strong>Example</strong> field helps the AI validate the format (e.g. "ORD-12345").
        </Tip>
      </section>

      {/* 5. Connector Chaining */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">5. Connector Chaining</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Sometimes you need one connector's output to feed into another. For example, a login/auth
          connector returns an access token that a second connector uses to fetch data.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">How it works</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li><strong>Connector A</strong> (e.g. "Authenticate CRM") calls a login endpoint and returns an <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">access_token</code>.</li>
          <li><strong>Connector B</strong> (e.g. "Get Bill Details") sets Auth Source to <em>From Input Param</em> and Auth Param Name to <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">access_token</code>.</li>
          <li>The AI calls Connector A first, then passes the token to Connector B automatically.</li>
        </ol>

        {/* Visual flow */}
        <div className="flex flex-col sm:flex-row items-center gap-3 py-4">
          <div className="px-4 py-3 rounded-lg bg-primary/10 border border-primary/30 text-center text-sm font-medium text-primary">
            Authenticate CRM
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-xs font-mono">
            <span className="hidden sm:inline">→</span>
            <span className="sm:hidden">↓</span>
            {' '}access_token{' '}
            <span className="hidden sm:inline">→</span>
            <span className="sm:hidden">↓</span>
          </div>
          <div className="px-4 py-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-center text-sm font-medium text-purple-600 dark:text-purple-400">
            Get Bill Details
          </div>
          <div className="text-gray-400 dark:text-gray-500 text-xs font-mono">
            <span className="hidden sm:inline">→</span>
            <span className="sm:hidden">↓</span>
            {' '}data{' '}
            <span className="hidden sm:inline">→</span>
            <span className="sm:hidden">↓</span>
          </div>
          <div className="px-4 py-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center text-sm font-medium text-green-600 dark:text-green-400">
            AI Agent
          </div>
        </div>

        <Tip>
          Use the <strong>Depends On</strong> field on Connector B to hint that it should run after Connector A.
        </Tip>
      </section>

      {/* 6. POST & PUT Write Operations */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          6. POST &amp; PUT Write Operations
        </h2>
        <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            POST and PUT connectors can <strong>create or modify data</strong> in your external systems.
            Use them with care — a misconfigured write connector could update customer records, create
            duplicate tickets, or trigger workflows unintentionally.
          </p>
        </div>
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">Best practices</h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <li>Write a very specific AI Decision Prompt — be explicit about when the AI should (and shouldn't) call it.</li>
          <li>Use required input parameters to make sure the AI collects all necessary data before calling.</li>
          <li>Test thoroughly in the Test Playground before enabling the connector.</li>
          <li>Consider starting with the connector disabled, then enabling it once you're confident.</li>
          <li>Monitor the call count and last-called timestamp on the connector card.</li>
        </ul>
      </section>

      {/* 7. Testing */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">7. Testing</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Every saved connector has a <strong>Test Playground</strong> tab. Here's the workflow:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <li>Save your connector first — the playground only appears for saved connectors.</li>
          <li>Fill in the test input values for each parameter.</li>
          <li>Click <strong>Run Test</strong> to see the live API response.</li>
          <li>Review the response and use <strong>Projection</strong> to select only the fields the AI should see — this reduces noise and improves response quality.</li>
        </ol>
        <Tip>
          Projection is powerful: if your API returns 50 fields but the AI only needs 5, select just those 5.
          This makes the AI faster and more accurate.
        </Tip>
      </section>

      {/* 8. Signed-in User Identity */}
      <section className="card p-6 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">8. Signed-in User Identity</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          If the site that embeds the widget already knows who is signed in, it can pass that identity to
          the widget as a short-lived signed token. The agent then treats the user as verified — no email
          code — and connectors can look up <strong>that user's own data</strong>. The conversation can
          never change who the user is.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">1. Add the verification key</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          The key is part of connecting the partner. On the Data Connectors page, open that partner's
          integration card and connect it — it asks for the key ID and identity secret along with the API key,
          and stores them together. Use the same API key your connector uses to call that partner, and paste
          each value exactly as your partner shows it. The API key it belongs to needs permission to read the
          signed-in user's orders or account, or the platform will refuse user-specific requests.
        </p>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">2. Pass the token to the widget</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          Keep your existing embed snippet and add one attribute, rendered on the server for signed-in users
          only. Leave it off for guests — the widget then works exactly as before.
        </p>
        <div className="overflow-x-auto rounded-lg bg-gray-900 dark:bg-black/40 border border-gray-800">
          <pre className="p-3 text-xs leading-relaxed text-gray-100"><code>{`<script src="…/widget.js"
        data-agent-id="YOUR_AGENT_ID"
        data-identity-token="SIGNED_TOKEN"></script>`}</code></pre>
        </div>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">3. Refresh it before it expires</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          Tokens last 60 minutes. Before that, and whenever the user signs in or out, send the new token.
          Once the widget has loaded you can call:
        </p>
        <div className="overflow-x-auto rounded-lg bg-gray-900 dark:bg-black/40 border border-gray-800">
          <pre className="p-3 text-xs leading-relaxed text-gray-100"><code>{`window.ProdagiWidget.identify(newToken); // refresh, or after sign-in
window.ProdagiWidget.identify(null);     // after sign-out`}</code></pre>
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          The event form is <strong>always safe</strong>: it works before the widget has finished loading, and
          even if the widget could not load at all. Use it in sign-out handlers.
        </p>
        <div className="overflow-x-auto rounded-lg bg-gray-900 dark:bg-black/40 border border-gray-800">
          <pre className="p-3 text-xs leading-relaxed text-gray-100"><code>{`document.dispatchEvent(new CustomEvent('prodagi:identify', { detail: { token: newToken } }));
document.dispatchEvent(new CustomEvent('prodagi:identify', { detail: { token: null } })); // sign-out`}</code></pre>
        </div>
        <Tip>
          <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">window.ProdagiWidget</code> only exists once the widget has loaded. Calling it
          earlier — or while the widget is unavailable — throws an error in your page. The event never does.
        </Tip>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">4. Use it in a connector</h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
          <li>
            <strong>Verified user identity</strong> parameter source — fills a parameter (email, user ID, name or
            organisation or marketplace ID) from the verified token.
          </li>
          <li>
            <strong>Forward the signed-in user's identity token</strong> — sends the token itself, in the header name
            your partner expects, alongside the connector's own credentials, so
            your API can check who is asking. Off by default; turn it on only for APIs that accept these tokens.
          </li>
        </ul>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
          If the user isn't signed in, the connector still calls your API — just without the token — and your API
          replies with a sign-in link. The agent passes that link on to the user. The link comes from your API's
          response, so there is nothing to set up for it here.
        </p>
        <div className="flex gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            Treat the token like a password: never put it in a URL, and never log it. It is only ever sent to
            connectors that have forwarding switched on.
          </p>
        </div>
      </section>

      {/* 9. Need Help? */}
      <section className="card p-6 space-y-3">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">9. Need Help?</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          If you have questions about setting up connectors, need help with authentication, or run into
          issues, reach out to us.
        </p>
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-primary" />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Contact AskProdigy AI support at{' '}
            <a href={SUPPORT_MAILTO} className="text-primary hover:underline font-medium">
              {SUPPORT_EMAIL}
            </a>
          </span>
        </div>
      </section>
    </div>
  );
};

export default DataConnectorDocs;
